import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useUnifiedTeamData } from "./Hooks/useUnifiedTeamData";
import { useTournamentAuth } from "../../utils/authorization";
import ScoringToolbar from "./ScoringToolbar/ScoringToolbar";
import AllTeamsScoresView from "./Views/AllTeamsScoresView";
import TeamsSkeleton from "./Views/TeamsSkeleton";
import ScoreReview from "./ScoreReview";
import { SinglePageTeamCreation } from "../createTeam";
import { AlertBox, SecondaryButton } from "../UI/DesignStandards";
import ScoringUploadPanel from "./ScoringUploadPanel";
import PrintSignupSheetDialog from "../SignupSheets/PrintSignupSheetDialog";
import PrintRecapSheetDialog from "../RecapSheets/PrintRecapSheetDialog";
import SignupSheetPreview from "../SignupSheets/SignupSheetPreview";
import { supabase } from "../../supabaseClient";
import { sanitizeErrorMessage } from "../../utility/errorSanitization";
import { sanitizeInput } from "../../utils/sanitization";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import {
  chooseSignupSheetFiles,
  extractSignupSheetFiles,
  signupExtractErrorMessage,
} from "../../utility/extractSignupSheets";
import {
  chooseRecapSheetFiles,
  extractRecapSheetFiles,
} from "../../utility/extractRecapSheets";
import {
  mergeSignupSheets,
  pendingSignupReviewEntries,
  withConsumedSignupKey,
  withSignupReviewPlan,
} from "../../utility/signupReview";
import {
  consumeSignupReviewKey,
  listPendingSignupSheetImports,
} from "../../utility/signupSheetImports";
import {
  EMPTY_RECAP_SHEET,
  buildRecapExtractFeedback,
  pendingScoreReviewTeams,
  recapExtractFinishedLabel,
  recapImportAverageWaitNote,
  recapImportNeedsAverageVerify,
} from "../../utility/recapSheets";
import {
  DEFAULT_DOUBLES_SINGLES_SETUP,
} from "../../utility/doublesSinglesSetup";
import { fetchDoublesSinglesSetup } from "../../utility/doublesSinglesSetupApi";
import {
  defaultSortOrderFor,
  sortScoringTeams,
} from "./ScoringToolbar/scoringSort";
import {
  FEATURE_AI_SHEET_IMPORT,
  tournamentHasAiFeature,
} from "../../utility/features";
import { scoringToBracketsHref } from "../../utility/adminNavigation";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";

const ScoringHub = React.memo(({ hideTitle = false }) => {
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tournament permissions
  const { canManage, canEnterScores } = useTournamentAuth(tournamentId);

  // State for filters (excluding search which is handled separately)
  const [filters, setFilters] = useState({
    squad: "all",
    event: "all",
    status: "all",
    date: "all",
    page: 1,
  });
  const [sortBy, setSortBy] = useState("lane");
  const [sortOrder, setSortOrder] = useState("asc");

  // Debounced search hook
  const [
    searchValue,
    debouncedSearchValue,
    setSearchValue,
    clearSearchValue,
    updateSearchValue,
  ] = useDebouncedSearch("");

  // Error state for better error handling
  const [error, setError] = useState(null);

  // Flag to track if we've already read initial URL parameters
  const hasReadInitialURLRef = useRef(false);
  const signupSheetsGenerationRef = useRef(0);

  // Read initial filters from URL search parameters (only on first load)
  useEffect(() => {
    // Only read URL parameters on the very first load
    if (hasReadInitialURLRef.current) {
      return;
    }
    hasReadInitialURLRef.current = true;

    try {
      // Sanitize URL parameters to prevent XSS and injection attacks
      const rawTeamId = searchParams.get("team");
      const rawSquad = searchParams.get("squad") || "all";
      const rawEvent = searchParams.get("event") || "all";
      const rawStatus = searchParams.get("status") || "all";
      const rawDate = searchParams.get("date") || "all";
      const rawSearch = searchParams.get("search") || "";

      // Sanitize all URL parameters
      const teamId = rawTeamId
        ? sanitizeInput(rawTeamId).substring(0, 50)
        : null;
      const squad = sanitizeInput(rawSquad).substring(0, 50);
      const event = sanitizeInput(rawEvent).substring(0, 50);
      const status = sanitizeInput(rawStatus).substring(0, 50);
      const date = sanitizeInput(rawDate).substring(0, 50);
      const search = sanitizeInput(rawSearch).substring(0, 100);

      // Handle team ID filter by setting search value
      if (teamId) {
        updateSearchValue(`team:${teamId}`);
      } else if (search) {
        updateSearchValue(search);
      }

      // Only update filters for non-search parameters
      if (
        squad !== "all" ||
        event !== "all" ||
        status !== "all" ||
        date !== "all"
      ) {
        const newFilters = {
          squad,
          event,
          status,
          date,
          page: 1,
        };
        setFilters(newFilters);
      }
    } catch (error) {
      console.error("Error reading URL parameters:", error);
      setError("Failed to load tournament filters");
    }
  }, []); // Only run once on mount

  // Simple search handler (no URL updates)
  const handleSearchChange = useCallback(
    (searchValue) => {
      setSearchValue(searchValue);
    },
    [setSearchValue]
  );

  // Simple filter handler (no URL updates)
  const setFiltersWithURL = useCallback((newFilters) => {
    setFilters(newFilters);
    // No URL updates - filters are local state only
  }, []);

  // Clear all filters (no URL updates)
  const clearAllFilters = useCallback(() => {
    const clearedFilters = {
      squad: "all",
      event: "all",
      status: "all",
      date: "all",
      page: 1,
    };
    setFilters(clearedFilters);
    clearSearchValue(); // Clear search value
    // No URL updates - just clear local state
  }, [clearSearchValue]);

  // Combine filters with debounced search for data fetching
  const filtersWithSearch = useMemo(
    () => ({
      ...filters,
      search: debouncedSearchValue,
    }),
    [filters, debouncedSearchValue]
  );

  const {
    data,
    loading,
    error: apiError,
    teams,
    hasData,
    teamsCount,
    updateScore,
    onTeamDelete,
    calculatingTotals,
    getCalculationResult,
    refetch,
    removeTeamFromState,
    updateTeamVerification,
    addTeamToState,
    patchTeamFields,
    canVerifyTeamForEdit,
    verificationStatus,
    isScoreSaving,
  } = useUnifiedTeamData(tournamentId, filtersWithSearch);

  // Get payout finalized status from tournament data
  const isPayoutFinalized = data?.tournament?.isPayoutFinalized || false;
  const [canImportSheets, setCanImportSheets] = useState(false);

  useEffect(() => {
    const organizationID = data?.tournament?.organizationID;
    if (!organizationID || !tournamentId) {
      setCanImportSheets(false);
      return undefined;
    }
    let cancelled = false;
    tournamentHasAiFeature(
      organizationID,
      FEATURE_AI_SHEET_IMPORT,
      tournamentId
    )
      .then((enabled) => {
        if (!cancelled) setCanImportSheets(Boolean(enabled));
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setCanImportSheets(false);
      });
    return () => {
      cancelled = true;
    };
  }, [data?.tournament?.organizationID, tournamentId]);

  // Handle team insertion from inline create team form
  const handleInsertTeam = useCallback(
    (position, newTeams) => {
      if (isPayoutFinalized) {
        return; // Don't allow inserting teams when payouts are finalized
      }
      // Add the new team to the local state without triggering a full refresh
      if (newTeams && addTeamToState) {
        addTeamToState(newTeams);
      }
    },
    [addTeamToState, isPayoutFinalized]
  );

  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showUploadTeams, setShowUploadTeams] = useState(false);
  const [showUploadScores, setShowUploadScores] = useState(false);
  const [showPrintSheets, setShowPrintSheets] = useState(false);
  const [showPrintRecaps, setShowPrintRecaps] = useState(false);
  const [uploadTeamFiles, setUploadTeamFiles] = useState(null);
  const [uploadScoreFiles, setUploadScoreFiles] = useState(null);
  const [uploadResetKey, setUploadResetKey] = useState(0);
  const [uploadTeamSquadId, setUploadTeamSquadId] = useState("");
  const [extractingTeams, setExtractingTeams] = useState(false);
  const [extractTeamProgress, setExtractTeamProgress] = useState(null);
  const [extractTeamError, setExtractTeamError] = useState("");
  const [extractTeamWarning, setExtractTeamWarning] = useState("");
  const [extractDisplaySheets, setExtractDisplaySheets] = useState(null);
  const [pendingSignupSheets, setPendingSignupSheets] = useState([]);
  const [showSignupReview, setShowSignupReview] = useState(false);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [signupReviewError, setSignupReviewError] = useState("");
  const [extractingScores, setExtractingScores] = useState(false);
  const [extractScoreProgress, setExtractScoreProgress] = useState(null);
  const [extractScoreError, setExtractScoreError] = useState("");
  const [extractScoreWarning, setExtractScoreWarning] = useState("");
  const [extractScoreFinishedLabel, setExtractScoreFinishedLabel] = useState("");
  const [extractScoreSummary, setExtractScoreSummary] = useState(null);
  const [recentImportedTeamIds, setRecentImportedTeamIds] = useState([]);
  const extractScoreRunRef = useRef(0);
  const extractScoreTokenRef = useRef(0);
  const [scoreReview, setScoreReview] = useState(null);
  const [signupReviewEvents, setSignupReviewEvents] = useState([]);
  const [doublesSinglesSetup, setDoublesSinglesSetup] = useState(
    DEFAULT_DOUBLES_SINGLES_SETUP
  );

  useEffect(() => {
    if (canImportSheets) return;
    setShowUploadTeams(false);
    setShowUploadScores(false);
    setShowSignupReview(false);
    setScoreReview(null);
  }, [canImportSheets]);

  const reviewEventList = useMemo(
    () =>
      signupReviewEvents.length > 0
        ? signupReviewEvents
        : data?.events || [],
    [signupReviewEvents, data?.events]
  );
  const reviewOptions = useMemo(
    () => ({ events: reviewEventList, setup: doublesSinglesSetup }),
    [reviewEventList, doublesSinglesSetup]
  );

  const reviewEntries = useMemo(
    () => pendingSignupReviewEntries(pendingSignupSheets, reviewOptions),
    [pendingSignupSheets, reviewOptions]
  );
  const currentReview = reviewEntries[reviewIndex] || null;
  useEffect(() => {
    if (!tournamentId || !canManage || !canImportSheets) return undefined;
    let cancelled = false;
    const generation = signupSheetsGenerationRef.current;
    listPendingSignupSheetImports(tournamentId)
      .then((sheets) => {
        if (cancelled || generation !== signupSheetsGenerationRef.current) {
          return;
        }
        setPendingSignupSheets((current) =>
          mergeSignupSheets(
            sheets,
            current.filter((sheet) => !sheet.importId)
          )
        );
      })
      .catch((error) => {
        console.error(error);
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId, canManage, canImportSheets]);

  useEffect(() => {
    if (!tournamentId || !canManage) return undefined;
    let cancelled = false;
    supabase
      .from("tournamentEvents")
      .select(
        "id, eventName, eventAbbreviation, isPrimary, bowlerQuantity, templateId, squadId"
      )
      .eq("tournamentID", tournamentId)
      .eq("isDeleted", false)
      .then(({ data: events, error }) => {
        if (cancelled || error) return;
        setSignupReviewEvents(events || []);
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId, canManage]);

  useEffect(() => {
    if (!tournamentId || !canManage) return undefined;
    let cancelled = false;
    fetchDoublesSinglesSetup(tournamentId)
      .then((next) => {
        if (!cancelled) setDoublesSinglesSetup(next);
      })
      .catch(() => {
        if (!cancelled) setDoublesSinglesSetup(DEFAULT_DOUBLES_SINGLES_SETUP);
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId, canManage]);

  useEffect(() => {
    if (reviewIndex > 0 && reviewIndex >= reviewEntries.length) {
      setReviewIndex(Math.max(0, reviewEntries.length - 1));
    }
  }, [reviewIndex, reviewEntries.length]);

  const pendingReviewTeams = useMemo(
    () => pendingScoreReviewTeams(teams),
    [teams]
  );
  const teamsById = useMemo(
    () =>
      Object.fromEntries((data?.teams || []).map((team) => [team.id, team])),
    [data?.teams]
  );
  const addedIndexById = useMemo(
    () => new Map((data?.teams || []).map((team, index) => [team.id, index])),
    [data?.teams]
  );
  const handleSortChange = useCallback((field) => {
    if (sortBy === field) {
      setSortOrder((order) => (order === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortOrder(defaultSortOrderFor(field));
  }, [sortBy]);
  const sortedTeams = useMemo(
    () =>
      sortScoringTeams(teams, sortBy, sortOrder, {
        addedIndex: (team) => addedIndexById.get(team.id) ?? 0,
        scoreForTeam: (team) => {
          const handicap = getCalculationResult?.(
            team.id,
            "team",
            "handicap"
          )?.teamHandicapTotal;
          if (handicap != null && Number.isFinite(Number(handicap))) {
            return Number(handicap);
          }
          const scratch = getCalculationResult?.(
            team.id,
            "team",
            "scratch"
          )?.teamScratchTotal;
          if (scratch != null && Number.isFinite(Number(scratch))) {
            return Number(scratch);
          }
          return null;
        },
      }),
    [
      teams,
      sortBy,
      sortOrder,
      addedIndexById,
      getCalculationResult,
    ]
  );

  const openScoreReview = useCallback(
    (teamId) => {
      const pending = pendingScoreReviewTeams(teams);
      if (!pending.length) return;
      const startIndex = Math.max(
        0,
        pending.findIndex((team) => team.id === teamId)
      );
      setShowSignupReview(false);
      setShowUploadTeams(false);
      setShowUploadScores(false);
      setReviewIndex(0);
      setScoreReview({
        startIndex,
        teamIds: pending.map((team) => team.id),
      });
    },
    [teams]
  );

  const closeSignupReview = useCallback(() => {
    setShowSignupReview(false);
    setReviewIndex(0);
    setSignupReviewError("");
  }, []);

  const closeUploadPanels = useCallback(() => {
    setShowUploadTeams(false);
    setShowUploadScores(false);
    setUploadTeamFiles(null);
    setUploadScoreFiles(null);
    setUploadTeamSquadId("");
    setExtractingTeams(false);
    setExtractTeamProgress(null);
    setExtractTeamError("");
    setExtractTeamWarning("");
    setExtractDisplaySheets(null);
    setExtractingScores(false);
    setExtractScoreProgress(null);
    setExtractScoreError("");
    setExtractScoreWarning("");
    setExtractScoreFinishedLabel("");
    setExtractScoreSummary(null);
    extractScoreRunRef.current = 0;
    extractScoreTokenRef.current += 1;
  }, []);

  const handleImportAnotherSignups = useCallback(() => {
    setUploadTeamFiles(null);
    setExtractDisplaySheets(null);
    setExtractTeamError("");
    setExtractTeamWarning("");
    setExtractTeamProgress(null);
    setUploadResetKey((key) => key + 1);
  }, []);

  const handleImportAnotherRecaps = useCallback(() => {
    setUploadScoreFiles(null);
    setExtractScoreSummary(null);
    setExtractScoreError("");
    setExtractScoreWarning("");
    setExtractScoreFinishedLabel("");
    setExtractScoreProgress(null);
    setUploadResetKey((key) => key + 1);
  }, []);

  const handleVerifyAverages = useCallback(() => {
    if (!tournamentId) return;
    navigate(tournamentPath(tournamentSlug, `/averages`));
  }, [navigate, tournamentId]);

  const openSignupReview = useCallback(() => {
    if (isPayoutFinalized) {
      return;
    }
    setShowCreateTeam(false);
    setShowUploadTeams(false);
    setShowUploadScores(false);
    setScoreReview(null);
    setShowSignupReview(true);
    setReviewIndex(0);
    setSignupReviewError("");
  }, [isPayoutFinalized]);

  const handleOpenCreateTeam = useCallback(() => {
    if (isPayoutFinalized) {
      return;
    }
    if (!data?.squads?.length || !data?.events?.length) {
      return;
    }
    closeUploadPanels();
    closeSignupReview();
    setShowCreateTeam(true);
  }, [
    isPayoutFinalized,
    data?.squads?.length,
    data?.events?.length,
    closeUploadPanels,
    closeSignupReview,
  ]);

  const handleOpenUploadTeams = useCallback(() => {
    if (!canImportSheets || isPayoutFinalized) {
      return;
    }
    if (!data?.squads?.length || !data?.events?.length) {
      return;
    }
    setShowCreateTeam(false);
    setShowUploadScores(false);
    setShowSignupReview(false);
    setScoreReview(null);
    setUploadScoreFiles(null);
    setExtractScoreError("");
    setExtractScoreWarning("");
    setExtractScoreFinishedLabel("");
    setExtractScoreSummary(null);
    setShowUploadTeams(true);
    setUploadTeamSquadId(
      filters.squad && filters.squad !== "all" ? filters.squad : ""
    );
    setExtractTeamError("");
    setExtractTeamWarning("");
    setExtractDisplaySheets(null);
  }, [
    canImportSheets,
    isPayoutFinalized,
    data?.squads?.length,
    data?.events?.length,
    filters.squad,
  ]);

  const handleUploadTeamFiles = useCallback((fileList) => {
    const { accepted, rejected } = chooseSignupSheetFiles(fileList);
    setUploadTeamFiles(accepted);
    setExtractDisplaySheets(null);
    setExtractTeamError(rejected.join(" "));
    setExtractTeamWarning("");
  }, []);

  const handleExtractTeams = useCallback(async () => {
    const files = Array.from(uploadTeamFiles || []);
    if (!files.length || !uploadTeamSquadId || !tournamentId) return;
    if (!canImportSheets) return;
    const organizationID = data?.tournament?.organizationID;
    if (!organizationID) {
      setExtractTeamError("Could not find this tournament's organization.");
      return;
    }
    setExtractingTeams(true);
    setExtractTeamProgress({ done: 0, total: files.length });
    setExtractTeamError("");
    setExtractTeamWarning("");
    setExtractDisplaySheets(null);
    try {
      const result = await extractSignupSheetFiles({
        organizationID,
        tournamentId,
        squadId: uploadTeamSquadId,
        files,
        onProgress: setExtractTeamProgress,
        prepareSheet: (sheet, events) =>
          withSignupReviewPlan(sheet, events, doublesSinglesSetup),
      });
      setExtractDisplaySheets(result.sheets);
      signupSheetsGenerationRef.current += 1;
      setPendingSignupSheets((current) =>
        mergeSignupSheets(current, result.savedSheets || [])
      );
      if (result.failures.length) {
        setExtractTeamError(result.failures.join(" "));
      }
      const entryCount = pendingSignupReviewEntries(result.savedSheets, {
        events: reviewEventList,
        setup: doublesSinglesSetup,
      }).length;
      if (!result.failures.length && entryCount === 0) {
        setExtractTeamWarning(
          "No names were found on this sheet. Check the photo and extract again."
        );
      }
    } catch (error) {
      console.error(error);
      setExtractTeamError(signupExtractErrorMessage(error));
    } finally {
      setExtractingTeams(false);
      setExtractTeamProgress(null);
    }
  }, [
    uploadTeamFiles,
    uploadTeamSquadId,
    tournamentId,
    data?.tournament?.organizationID,
    reviewEventList,
    doublesSinglesSetup,
    canImportSheets,
  ]);

  const consumeCurrentSignupReview = useCallback(
    async (failedMessage) => {
      const entry = currentReview;
      if (!entry) return;
      const events = reviewEventList;
      const setup = doublesSinglesSetup;
      const wasLast = reviewIndex + 1 >= reviewEntries.length;
      const nextSheets = withConsumedSignupKey(
        pendingSignupSheets,
        entry.sheet,
        entry.key
      );
      const remainingForSheet = pendingSignupReviewEntries(nextSheets, {
        events,
        setup,
      }).filter(
        (item) =>
          (entry.sheet?.importId &&
            item.sheet?.importId === entry.sheet.importId) ||
          (!entry.sheet?.importId &&
            item.sheet?.storagePath === entry.sheet?.storagePath)
      ).length;
      try {
        if (entry.sheet?.importId) {
          await consumeSignupReviewKey(entry.sheet.importId, entry.key, {
            completed: remainingForSheet === 0 && events.length > 0,
          });
        }
        signupSheetsGenerationRef.current += 1;
        const stored =
          remainingForSheet === 0 && events.length > 0
            ? nextSheets.filter((sheet) =>
                entry.sheet?.importId
                  ? sheet.importId !== entry.sheet.importId
                  : sheet.storagePath !== entry.sheet?.storagePath
              )
            : nextSheets;
        setPendingSignupSheets(stored);
        setSignupReviewError("");
        const remaining = pendingSignupReviewEntries(stored, { events, setup });
        if (wasLast || remaining.length === 0) {
          closeSignupReview();
        }
      } catch (error) {
        console.error(error);
        setSignupReviewError(failedMessage);
      }
    },
    [
      currentReview,
      pendingSignupSheets,
      reviewEventList,
      doublesSinglesSetup,
      reviewIndex,
      reviewEntries.length,
      closeSignupReview,
    ]
  );

  const handlePaperTeamSaved = useCallback(async () => {
    await consumeCurrentSignupReview(
      "The team was created. Could not mark this signup as done."
    );
  }, [consumeCurrentSignupReview]);

  const handlePaperTeamDelete = useCallback(async () => {
    await consumeCurrentSignupReview(
      "Could not remove this signup from Review signups."
    );
  }, [consumeCurrentSignupReview]);

  const handlePaperTeamSkip = useCallback(() => {
    if (reviewIndex + 1 >= reviewEntries.length) {
      closeSignupReview();
      return;
    }
    setReviewIndex((index) => index + 1);
  }, [reviewIndex, reviewEntries.length, closeSignupReview]);

  const handleOpenUploadScores = useCallback(() => {
    if (!canImportSheets || isPayoutFinalized) {
      return;
    }
    setShowCreateTeam(false);
    setShowUploadTeams(false);
    setShowSignupReview(false);
    setUploadTeamFiles(null);
    setShowUploadScores(true);
    setExtractScoreError("");
    setExtractScoreWarning("");
    setExtractScoreFinishedLabel("");
    setExtractScoreSummary(null);
  }, [canImportSheets, isPayoutFinalized]);

  const handleUploadScoreFiles = useCallback((fileList) => {
    const { accepted, rejected } = chooseRecapSheetFiles(fileList);
    setUploadScoreFiles(accepted);
    setExtractScoreSummary(null);
    setExtractScoreError(rejected.join(" "));
    setExtractScoreWarning("");
    setExtractScoreFinishedLabel("");
  }, []);

  const handleExtractScores = useCallback(async () => {
    const files = Array.from(uploadScoreFiles || []);
    if (!files.length || !tournamentId) return;
    if (!canImportSheets) return;
    const organizationID = data?.tournament?.organizationID;
    if (!organizationID) {
      setExtractScoreError("Could not find this tournament's organization.");
      return;
    }
    const extractToken = extractScoreTokenRef.current + 1;
    extractScoreTokenRef.current = extractToken;
    setExtractingScores(true);
    setExtractScoreProgress({ done: 0, total: files.length });
    setExtractScoreError("");
    setExtractScoreWarning("");
    setExtractScoreFinishedLabel("");
    setExtractScoreSummary(null);
    try {
      const result = await extractRecapSheetFiles({
        organizationID,
        tournamentId,
        files,
        onProgress: setExtractScoreProgress,
      });
      const feedback = buildRecapExtractFeedback({
        result,
        teams: data?.teams,
      });
      setExtractScoreSummary(feedback.summary);
      const importedIds = feedback.summary?.importedTeamIds || [];
      if (Number(result.importedScores) > 0 && importedIds.length) {
        setRecentImportedTeamIds(importedIds);
      }
      if (result.failures.length) {
        setExtractScoreError(result.failures.join(" "));
      }
      if (!result.failures.length && result.importedScores === 0 && feedback.banner) {
        setExtractScoreWarning(feedback.banner);
      }
      if (result.importedScores > 0 && refetch) {
        refetch();
      }
    } catch (error) {
      console.error(error);
      setExtractScoreError(
        error.message || "Could not extract this recap sheet."
      );
    } finally {
      if (extractScoreTokenRef.current === extractToken) {
        extractScoreRunRef.current += 1;
        setExtractScoreFinishedLabel(
          recapExtractFinishedLabel({
            run: extractScoreRunRef.current,
            at: new Date().toISOString(),
          })
        );
        setExtractingScores(false);
        setExtractScoreProgress(null);
      }
    }
  }, [
    uploadScoreFiles,
    tournamentId,
    data?.tournament?.organizationID,
    data?.teams,
    refetch,
    canImportSheets,
  ]);

  const handleOpenPrintSheets = useCallback(() => {
    if (!data?.events?.length) {
      return;
    }
    setShowPrintSheets(true);
  }, [data?.events?.length]);

  const handleOpenPrintRecaps = useCallback(() => {
    if (!data?.squads?.length) {
      return;
    }
    setShowPrintRecaps(true);
  }, [data?.squads?.length]);

  // Handle team creation from toolbar - refresh data when a new team is created
  const handleTeamCreated = useCallback(() => {
    if (refetch) {
      refetch();
    }
    setShowCreateTeam(false);
  }, [refetch]);

  // Memoize resultInfo to prevent unnecessary re-renders
  const resultInfo = useMemo(() => {
    if (!data?.tournament) return null;
    return {
      tournamentName: data.tournament.name,
      tournamentDate: data.tournament.date,
      totalTeams: teamsCount,
    };
  }, [data?.tournament, teamsCount]);

  // Memoize handleTeamDelete to prevent unnecessary re-renders
  const handleTeamDelete = useCallback(
    async (teamId) => {
      if (isPayoutFinalized) {
        return; // Don't allow deleting teams when payouts are finalized
      }
      try {
        // Optimistically remove the team from local state for instant UI update
        removeTeamFromState(teamId);
      } catch (error) {
        console.error("Error removing team from state:", error);
        setError(
          "Failed to remove team from display. Please refresh the page."
        );
        // If there's an error, we could refetch to restore the original state
        // but for now, we'll just log the error
      }
    },
    [removeTeamFromState, isPayoutFinalized]
  );

  const handleRecapDelete = useCallback(
    (teamId) => {
      if (isPayoutFinalized) {
        return;
      }
      patchTeamFields(teamId, EMPTY_RECAP_SHEET);
      setScoreReview((current) => {
        if (!current) return current;
        const nextIds = current.teamIds.filter((id) => id !== teamId);
        if (!nextIds.length) return null;
        return { ...current, teamIds: nextIds };
      });
    },
    [patchTeamFields, isPayoutFinalized]
  );

  // Handle team verification changes without clearing calculations
  const handleVerificationChange = useCallback(
    async (teamId, isVerified) => {
      if (isPayoutFinalized) {
        return; // Don't allow verification changes when payouts are finalized
      }
      try {
        // Update the local team state to reflect verification change
        // This prevents the need for a full refetch while maintaining UI consistency
        updateTeamVerification(teamId, isVerified);
      } catch (error) {
        console.error("Error updating verification state:", error);
        // Don't set error state for verification changes as they're not critical
      }
    },
    [updateTeamVerification, isPayoutFinalized]
  );

  // Use the updateScore function directly from useUnifiedTeamData
  const handleScoreUpdate = useCallback(
    async (teamId, bowlerId, gameNumber, score) => {
      if (isPayoutFinalized) {
        return; // Don't allow score updates when payouts are finalized
      }
      try {
        await updateScore(teamId, bowlerId, gameNumber, score);
        setError(null); // Clear any previous errors on successful update
      } catch (error) {
        console.error("Error updating score:", error);
        setError(sanitizeErrorMessage(error, "save score"));
      }
    },
    [updateScore, isPayoutFinalized]
  );

  // Generate shareable link with current filters and search
  const generateShareableLink = useCallback(() => {
    const params = new URLSearchParams();

    // Add non-search filters to URL
    if (filters.squad !== "all") params.set("squad", filters.squad);
    if (filters.event !== "all") params.set("event", filters.event);
    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.date !== "all") params.set("date", filters.date);

    // Add search to URL
    if (searchValue) {
      if (searchValue.startsWith("team:")) {
        params.set("team", searchValue.substring(5));
      } else {
        params.set("search", searchValue);
      }
    }

    const baseUrl = window.location.origin + window.location.pathname;
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }, [filters, searchValue]);

  const shouldShowSkeleton = loading || !data || !data.teams;

  return (
    <div>
      <ScoringToolbar
        filters={{ ...filters, search: searchValue }} // Use immediate search value for UI
        onFiltersChange={setFiltersWithURL}
        onSearchChange={handleSearchChange}
        onClearFilters={clearAllFilters}
        onCopyLink={generateShareableLink}
        data={data}
        onTeamCreated={handleTeamCreated}
        onCreateTeam={handleOpenCreateTeam}
        onPrintSheets={handleOpenPrintSheets}
        onPrintRecaps={handleOpenPrintRecaps}
        onUploadTeams={handleOpenUploadTeams}
        onUploadScores={handleOpenUploadScores}
        onReviewScores={
          canImportSheets && pendingReviewTeams.length
            ? () => openScoreReview(pendingReviewTeams[0].id)
            : undefined
        }
        reviewCount={canImportSheets ? pendingReviewTeams.length : 0}
        onReviewTeams={
          canImportSheets && reviewEntries.length && !isPayoutFinalized
            ? openSignupReview
            : undefined
        }
        teamReviewCount={canImportSheets ? reviewEntries.length : 0}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        canManage={canManage}
        canImportSheets={canImportSheets}
        isPayoutFinalized={isPayoutFinalized}
        hideTitle={hideTitle}
        bracketsHref={scoringToBracketsHref(tournamentId, filters.squad)}
      />

      <PrintSignupSheetDialog
        isOpen={showPrintSheets}
        onClose={() => setShowPrintSheets(false)}
        tournamentId={tournamentId}
        tournamentName={data?.tournament?.name}
        events={data?.events || []}
      />

      <PrintRecapSheetDialog
        isOpen={showPrintRecaps}
        onClose={() => setShowPrintRecaps(false)}
        tournamentId={tournamentId}
        tournamentName={data?.tournament?.name}
        squads={data?.squads || []}
        events={data?.events || []}
        teams={data?.teams || []}
        defaultSquadId={filters.squad !== "all" ? filters.squad : ""}
      />

      <ScoreReview
        isOpen={Boolean(scoreReview)}
        onClose={() => setScoreReview(null)}
        teamIds={scoreReview?.teamIds || []}
        startIndex={scoreReview?.startIndex || 0}
        teamsById={teamsById}
        tournamentId={tournamentId}
        canEnterScores={canEnterScores}
        canVerifyTeamForEdit={canVerifyTeamForEdit}
        verificationStatus={verificationStatus}
        onScoreUpdate={handleScoreUpdate}
        onVerificationChange={handleVerificationChange}
        calculatingTotals={calculatingTotals}
        getCalculationResult={getCalculationResult}
        isScoreSaving={isScoreSaving}
        canManage={canManage}
        onRecapDelete={handleRecapDelete}
        importedTeamIds={recentImportedTeamIds}
      />

      {showUploadTeams &&
        canManage &&
        canImportSheets &&
        !isPayoutFinalized &&
        data?.squads?.length > 0 &&
        data?.events?.length > 0 && (
          <ScoringUploadPanel
            title="Import signups"
            description="Choose the squad these signups belong to, then choose files. Extract reads the event from the sheet. Review signups on Scoring from any device."
            files={uploadTeamFiles}
            onFilesChange={handleUploadTeamFiles}
            onCancel={closeUploadPanels}
            squads={data.squads || []}
            squadId={uploadTeamSquadId}
            onSquadChange={setUploadTeamSquadId}
            accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
            onExtract={handleExtractTeams}
            extracting={extractingTeams}
            extractProgress={extractTeamProgress}
            extractError={extractTeamError}
            extractWarning={extractTeamWarning}
            extractSheets={extractDisplaySheets}
            events={reviewEventList}
            setup={doublesSinglesSetup}
            onReviewTeams={openSignupReview}
            onImportAnother={handleImportAnotherSignups}
            fileResetKey={uploadResetKey}
          />
        )}

      {showSignupReview &&
        canManage &&
        canImportSheets &&
        !isPayoutFinalized &&
        currentReview && (
          <div className="mb-6 space-y-4">
            {signupReviewError ? (
              <AlertBox
                type="error"
                title="Could not update review"
                message={signupReviewError}
              />
            ) : null}
            <div className="flex flex-col gap-4">
              <SignupSheetPreview
                storagePath={currentReview.sheet?.storagePath}
                mimeType={currentReview.sheet?.mimeType}
                fileName={currentReview.sheet?.fileName}
                sourcePage={currentReview.sheet?.sourcePage}
                highlightName={currentReview.highlightName}
                highlightNames={[
                  currentReview.teamName,
                  ...(currentReview.rows || []).map((row) => row?.name),
                ].filter(Boolean)}
                boundingBox={currentReview.boundingBox}
                compact
              />
              <SinglePageTeamCreation
                squadID={currentReview.squadId}
                tournamentID={tournamentId || ""}
                setTeams={(newTeams) => {
                  if (newTeams && addTeamToState) {
                    addTeamToState(newTeams);
                  }
                }}
                onClose={closeSignupReview}
                squads={data?.squads || []}
                teams={teams || []}
                paperSignup={currentReview}
                paperIndex={reviewIndex}
                paperCount={reviewEntries.length}
                onPaperTeamSaved={handlePaperTeamSaved}
                onPaperTeamSkip={handlePaperTeamSkip}
                onPaperTeamDelete={handlePaperTeamDelete}
              />
            </div>
          </div>
        )}

      {showUploadScores && canManage && canImportSheets && !isPayoutFinalized && (
        <ScoringUploadPanel
          title="Import recaps"
          description="Choose files. Extract writes scores onto the matching teams from the BTA Use code."
          files={uploadScoreFiles}
          onFilesChange={handleUploadScoreFiles}
          onCancel={closeUploadPanels}
          accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
          onExtract={handleExtractScores}
          extracting={extractingScores}
          extractProgress={extractScoreProgress}
          extractError={extractScoreError}
          extractWarning={extractScoreWarning}
          extractWarningTitle="No scores imported"
          extractFinishedLabel={extractScoreFinishedLabel}
          extractSummary={extractScoreSummary}
          onReviewScores={
            pendingReviewTeams.length
              ? () => openScoreReview(pendingReviewTeams[0].id)
              : undefined
          }
          reviewCount={pendingReviewTeams.length}
          onVerifyAverages={handleVerifyAverages}
          averagesPending={recapImportNeedsAverageVerify(
            data?.teams,
            extractScoreSummary?.importedTeamIds
          )}
          averageWaitNote={recapImportAverageWaitNote(
            data?.teams,
            extractScoreSummary?.importedTeamIds
          )}
          onImportAnother={handleImportAnotherRecaps}
          fileResetKey={uploadResetKey}
        />
      )}

      {/* Create Team Form from Toolbar - At the top */}
      {showCreateTeam &&
        canManage &&
        !isPayoutFinalized &&
        data?.squads?.length > 0 &&
        data?.events?.length > 0 && (
          <div className="card-board p-6 mb-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-end mb-4">
                <SecondaryButton onClick={() => setShowCreateTeam(false)}>
                  Cancel
                </SecondaryButton>
              </div>
              <SinglePageTeamCreation
                squadID={data?.squads?.[0]?.id || ""}
                tournamentID={tournamentId || ""}
                setTeams={(newTeams, shouldClose = true) => {
                  // Team was created successfully
                  if (shouldClose) {
                    setShowCreateTeam(false);
                  }
                  // Add the new team to the local state without triggering a full refresh
                  if (newTeams && addTeamToState) {
                    addTeamToState(newTeams);
                  }
                }}
                onClose={() => setShowCreateTeam(false)}
                squads={data?.squads || []}
                teams={teams || []}
              />
            </div>
          </div>
        )}

      {error && (
        <div className="p-6">
          <div
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
            role="alert"
            aria-live="polite"
          >
            <div className="text-red-800 font-medium">
              {sanitizeErrorMessage(error, "load tournament data")}
            </div>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              aria-label="Dismiss error message"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {(apiError || error) && (
        <div className="p-6">
          <div
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
            role="alert"
            aria-live="polite"
          >
            <div className="text-red-800 font-medium">
              {apiError
                ? sanitizeErrorMessage(apiError, "load tournament data")
                : sanitizeErrorMessage(error, "load tournament data")}
            </div>
            <button
              onClick={() => {
                setError(null);
                if (refetch) refetch();
              }}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              aria-label="Dismiss error and retry"
            >
              Dismiss and Retry
            </button>
          </div>
        </div>
      )}

      {shouldShowSkeleton ? (
        <div
          className="p-6"
          aria-live="polite"
          aria-label="Loading tournament data"
        >
          <TeamsSkeleton />
        </div>
      ) : (
        <main aria-label="Tournament scoring interface">
          <AllTeamsScoresView
            teams={sortedTeams}
            onScoreUpdate={handleScoreUpdate}
            onTeamDelete={handleTeamDelete}
            onVerificationChange={handleVerificationChange}
            calculatingTotals={calculatingTotals}
            getCalculationResult={getCalculationResult}
            onInsertTeam={handleInsertTeam}
            tournamentId={tournamentId}
            squadId={data?.squads?.[0]?.id || ""}
            squads={data?.squads || []}
            events={data?.events || []}
            canManage={canManage}
            canEnterScores={canEnterScores}
            isPayoutFinalized={isPayoutFinalized}
            canVerifyTeamForEdit={canVerifyTeamForEdit}
            verificationStatus={verificationStatus}
            isScoreSaving={isScoreSaving}
            onReview={canImportSheets ? openScoreReview : undefined}
            onRecapDelete={canImportSheets ? handleRecapDelete : undefined}
            importedTeamIds={recentImportedTeamIds}
          />
        </main>
      )}
    </div>
  );
});

ScoringHub.displayName = "ScoringHub";

export default ScoringHub;
